import { matchPath } from 'react-router-dom';
import type { Organization, UserRole } from '@/api/models';
import { PATH } from './PATH';

/** Render the matched route, or replace the URL with another one. */
export type OnboardingGateDecision = 'render' | { redirect: string };

type OnboardingGateInput = {
    organisation: Pick<Organization, 'onboardedAt'>;
    /** The signed-in user's role; `null` when the session carries no user. */
    role: UserRole | null;
    pathname: string;
};

/** The wizard and every step under it (a segment prefix, not a string one). */
export const isOnboardingPath = (pathname: string): boolean =>
    matchPath({ path: PATH.ONBOARDING, end: false }, pathname) !== null;

export const isSetupPendingPath = (pathname: string): boolean =>
    matchPath(PATH.SETUP_PENDING, pathname) !== null;

export const isOrganisationOnboarded = (
    organisation: Pick<Organization, 'onboardedAt'>,
): boolean => organisation.onboardedAt !== null;

/**
 * Where a signed-in user may be, given their organisation and role.
 *
 * | Organisation   | Role            | On the wizard | On setup-pending | Elsewhere      |
 * |----------------|-----------------|---------------|------------------|----------------|
 * | not onboarded  | OrgAdmin        | render        | → wizard         | → wizard       |
 * | not onboarded  | any other       | → pending     | render           | → pending      |
 * | onboarded      | any             | → home        | → home           | render         |
 *
 * Only an OrgAdmin can onboard, so everyone else waits on setup-pending and
 * reaches no shell route by URL. Once onboarded, both onboarding pages are
 * closed: the wizard runs once per organisation.
 */
export const decideOnboardingRoute = ({
    organisation,
    role,
    pathname,
}: OnboardingGateInput): OnboardingGateDecision => {
    const onWizard = isOnboardingPath(pathname);
    const onPending = isSetupPendingPath(pathname);

    if (isOrganisationOnboarded(organisation)) {
        return onWizard || onPending ? { redirect: PATH.HOME } : 'render';
    }

    if (role === 'OrgAdmin') {
        return onWizard ? 'render' : { redirect: PATH.ONBOARDING };
    }

    return onPending ? 'render' : { redirect: PATH.SETUP_PENDING };
};
